import React, { ChangeEvent, Component, ReactNode } from 'react';
import { AnimatedButton, Icon } from '@trlogic/ui-pack';
import { SandboxHistorySchema, TransactionJsonModel, TransactionRequest, TransactionRequestType } from '../../domain/stream/transaction/TransactionDomains';
import Editor from '@monaco-editor/react';
import CodeSnippetsComponent from './code-snippets/CodeSnippetsComponent';
import DataType from '../../constant/DataType';
import TransactionService from '../../service/TransactionService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import EnvironmentUtil from '../../util/EnvironmentUtil';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import {
  ArrayVariableProperties,
  CustomStructureProperties,
  DateVariableProperties,
  ObjectVariableProperties,
  VariableProperties,
} from '../../domain/datatype/VariablePropertiesDomains';
import { DataStructure } from '../../domain/structure/DataStructureDomains';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import CustomLoading from '../custom/CustomLoading';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import FormicaInput from '../custom/FormicaInput';
import { Dialog } from 'primereact/dialog';
import FolderService from '../../service/FolderService';
import ComponentName from '../../constant/ComponentName';
import { RootState, store } from '../../redux/store';
import { FormicaProps, withFormica } from 'helpers/withFormica';
import { initialPagePath } from '../../redux/reducers/PageReducer';
import LanguageUtil from '../../util/LanguageUtil';
import DateFormatUtil from '../../util/DateFormatUtil';
import SandboxHistoryContainer from './history/SandboxHistoryContainer';
import { setHistories } from '../../redux/actions/SandboxActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import moment from 'moment';
import eq = query.eq;
import QueryBuilder = query.QueryBuilder;

class SandboxApiContainer extends Component<Props, State> {
  public state = new State();

  private eventService: TransactionService = ServiceContainer.getInstance().get(ServiceNames.CHANNEL);
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private dataStructureSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  public render(): ReactNode {
    const { selectedTransaction, request, response, key, loading, requestType, codeSnippetVisible, id } = this.state;
    return (
      <div className={'flex flex-col lg:flex-row w-full overflow-x-hidden  h-full py-5 px-5 gap-5 overflow-y-scroll max-h-[calc(100dvh-260px)]'}>
        <div className={'flex lg:w-1/3 lg:max-w-[33%] w-full h-full order-1 overflow-hidden lg:order-0'}>
          <SandboxHistoryContainer setHistory={this.setHistoryItem} />
        </div>
        <div className={'flex flex-col w-full h-full gap-y-2 lg:order-1'}>
          <div className={'flex items-center justify-between gap-x-5 gap-y-5'}>
            <div className={'flex gap-x-2 items-center w-full'}>
              <div className={'flex items-center w-1/2 max-h-[34px] gap-x-2 rounded-sm border-[1px] border-[#E0E4E8]'}>
                <div className={'flex h-full'}>
                  <FormicaSearchSelectBox
                    placeholder={LanguageUtil.get({ id: 'common.input.placeholder.method', defaultMessage: 'Method' })}
                    disabled={!RoleHelper.canEdit('SandboxSimulator')}
                    rounded
                    options={this.prepareMethodSelectBox()}
                    value={requestType}
                    onChange={(e: FormicaSelectBoxItem) => this.onChangeSelectedMethod(e.value)}
                    data-testid={`${ComponentName.SANDBOX}_method_select`}
                  />
                </div>
                <div className={'flex h-full w-full border-r px-2 py-2 text-f-black-3 max-h-[34px] max-w-[650px] overflow-scroll'}>
                  {id && requestType == 'DELETE' ? `${this.environmentUtil.getTransactionUrl()}/${id}` : this.environmentUtil.getTransactionUrl()}
                </div>
              </div>
              <div className={'flex items-center w-1/2'}>
                {requestType && ['SUBMIT', 'STORE', 'RESUBMIT', 'UPDATE'].includes(requestType) && (
                  <FormicaSearchSelectBox
                    placeholder={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' })}
                    disabled={!RoleHelper.canEdit('SandboxSimulator')}
                    rounded
                    options={this.prepareTransactionSelectBox()}
                    value={selectedTransaction?.name}
                    onChange={(e: FormicaSelectBoxItem) => this.onChangeSelectedTransaction(e.value)}
                    data-testid={`${ComponentName.SANDBOX}_channel_select`}
                  />
                )}
                {requestType && ['SUBMIT', 'STORE'].includes(requestType) && (
                  <div className='px-2 w-full'>
                    <FormicaInput
                      disabled={!RoleHelper.canEdit('SandboxSimulator')}
                      placeholder={LanguageUtil.get({ id: 'pages.sandbox.api.key', defaultMessage: 'key' })}
                      value={key || ''}
                      onChange={this.onChangeKey}
                      data-testid={`${ComponentName.SANDBOX}_key_input`}
                    />
                  </div>
                )}
                {requestType && ['UPDATE', 'DELETE', 'RESUBMIT'].includes(requestType) && (
                  <div className='px-2 w-full'>
                    <FormicaInput
                      disabled={!RoleHelper.canEdit('SandboxSimulator')}
                      placeholder={LanguageUtil.get({ id: 'pages.sandbox.api.id', defaultMessage: 'Id' })}
                      value={id || ''}
                      onChange={this.onChangeId}
                      data-testid={`${ComponentName.SANDBOX}_id_input`}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={'flex gap-x-3 items-center justify-end '}>
              <div
                className={'flex w-10 h-10 rounded-sm border-[1px] border-[#E0E4E8] default-shadow items-center justify-center cursor-pointer'}
                onClick={(e) => {
                  this.setState({ codeSnippetVisible: true });
                }}
              >
                <Icon icon={'code'} size={16} />
              </div>
              <div className={'flex'}>
                <AnimatedButton
                  disabled={!RoleHelper.canEdit('SandboxSimulator')}
                  customType={'primary'}
                  onClick={this.sendEvent}
                  icon={'deploy'}
                  text={LanguageUtil.get({ id: 'common.button.send', defaultMessage: 'Send' })}
                  data-testid={`${ComponentName.SANDBOX}_send_button`}
                />
              </div>
            </div>
          </div>
          <div className={'flex h-full w-full items-center gap-x-5'}>
            <div className={'flex border rounded-lg w-1/2 h-full px-5 py-5'}>
              <div className={'flex flex-col gap-y-5 h-full w-full'} data-testid={`${ComponentName.SANDBOX}_request_container`}>
                <div className={'text-f-black-3'}>{LanguageUtil.get({ id: 'pages.sandbox.api.request', defaultMessage: 'Request' })}</div>
                {loading ? (
                  <CustomLoading />
                ) : (
                  <Editor
                    options={{ readOnly: !RoleHelper.canEdit('SandboxSimulator') || requestType == 'DELETE' }}
                    key={'request'}
                    language={'json'}
                    onChange={this.onChangeRequest}
                    value={request || ''}
                  />
                )}
              </div>
            </div>
            <div className={'flex border rounded-lg w-1/2 h-full px-5 py-5'}>
              <div className={'flex flex-col gap-y-5 h-full w-full'} data-testid={`${ComponentName.SANDBOX}_response_container`}>
                <div className={'flex items-center justify-between text-f-black-3'}>
                  <div className={'w-full flex'}>
                    <p>{LanguageUtil.get({ id: 'pages.sandbox.api.response', defaultMessage: 'Response' })}</p>
                  </div>
                  <div className={'flex items-center justify-end gap-x-5 w-full self-end'}>
                    {(response?.status || response?.responseTime != undefined) && (
                      <>
                        <div className={'flex'}>
                          <p className={'flex'}>Status: {this.getStatus(response?.status?.toString() || '200')}</p>
                        </div>
                        <div className={'flex'}>
                          <p className={'flex'}>Time: {this.getTime(response?.responseTime || 0)}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <Editor key={'response'} language={'json'} value={JSON.stringify(response?.data, null, 2)} />
              </div>
            </div>
          </div>
          <Dialog
            header={LanguageUtil.get({ id: 'pages.sandbox.api.codeSnippets', defaultMessage: 'Code Snippets' })}
            className={'w-5/6 h-5/6'}
            visible={codeSnippetVisible}
            onHide={() => this.setState({ codeSnippetVisible: false })}
            dismissableMask
            closable
            closeOnEscape
          >
            <CodeSnippetsComponent request={request} />
          </Dialog>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location },
    } = this.props;
    const page = store.getState().page;
    const initial = initialPagePath[ComponentName.SANDBOX];
    if (page.pagePath.length == 0 || page.pagePath[0].title != initial?.title) {
      await FolderService.calculate('WITHOUT_FOLDER', ComponentName.SANDBOX, location.pathname, store.getState().page);
    }
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const query = QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build();
    this.dataStructureSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, query);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.dataStructureSubscription]);
  };

  private stopTracker = () => {
    this.dataStructureSubscription?.stop();
    this.computation?.stop();
  };

  private tracker = () => {
    if (this.dataStructureSubscription?.ready()) {
      this.setState({ loading: false });
    }
  };

  private prepareTransactionSelectBox = () => {
    const { transactions } = this.props;
    return transactions.map((transaction: TransactionJsonModel) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  };

  private prepareMethodSelectBox = (): Array<{ label: TransactionRequestType; value: TransactionRequestType }> => {
    return [
      { label: 'SUBMIT', value: 'SUBMIT' },
      { label: 'RESUBMIT', value: 'RESUBMIT' },
      { label: 'STORE', value: 'STORE' },
      { label: 'UPDATE', value: 'UPDATE' },
      { label: 'DELETE', value: 'DELETE' },
    ];
  };

  private setHistoryItem = (history: SandboxHistorySchema) => {
    this.setState({ requestType: history.requestType, key: history.body.key, request: JSON.stringify(history.body, null, 2) });
    if (history.requestType == 'DELETE') {
      this.setState({ id: history.transactionName });
    }
    const { transactions } = this.props;
    const selectedTransaction = transactions.find((transaction: TransactionJsonModel) => transaction.name === history.transactionName);
    this.setState({ selectedTransaction });
    // this.onChangeSelectedTransaction(history.transactionName); // TODO FIX THIS
  };

  private getStatus = (statusCode: string) => {
    switch (statusCode) {
      case '200':
        return <div className={'text-green-700'}>{statusCode} Success</div>;
      case '400':
        return <div className={'text-orange-700'}>{statusCode} Bad Request</div>;
      case '401':
        return <div className={'text-orange-700'}>{statusCode} Unauthorized </div>;
      case '500':
        return <div className={'text-red-700'}>{statusCode} Interval Error</div>;
      default:
        return <div className={'text-red-700'}>{statusCode}</div>;
    }
  };

  private getTime = (time: string) => {
    return <div className={'text-[#5E9864]'}>{time} ms</div>;
  };

  private onChangeSelectedTransaction = async (name: string) => {
    const { transactions } = this.props;
    const { key, requestType, id } = this.state;
    const selectedTransaction = transactions.find((transaction: TransactionJsonModel) => transaction.name === name);
    let variables: Record<string, any> = {};
    let request: TransactionRequest;
    if (requestType == 'SUBMIT' || requestType == 'STORE') {
      request = { name: name, key: key || '', variables: variables };
    } else if (requestType == 'RESUBMIT' || requestType == 'UPDATE') {
      request = { name: name, id: id || '', variables: variables };
    }
    if (selectedTransaction) {
      for (const variable of selectedTransaction?.variables) {
        variables[variable.name] = await this.getVariableValueByType(variable.properties);
      }
    }
    this.setState({ selectedTransaction, request: JSON.stringify(request!, null, 2) });
  };

  private onChangeSelectedMethod = async (value: TransactionRequestType) => {
    this.setState({ requestType: value }, () => this.resetState());
  };

  private getVariableValueByType = async (properties: VariableProperties): Promise<Record<string, number> | Array<any> | string | number | boolean> => {
    const { dataType } = properties;
    switch (dataType) {
      case DataType.STRING:
        return '';
      case DataType.NUMBER:
        return 0;
      case DataType.BOOLEAN:
        return false;
      case DataType.OBJECT:
        const obj: Record<string, any> = {};
        for (const entry of (properties as ObjectVariableProperties).variables) {
          obj[entry.name] = await this.getVariableValueByType(entry.properties);
        }
        return obj;
      case DataType.ARRAY:
        return [await this.getVariableValueByType((properties as ArrayVariableProperties).properties as VariableProperties)];
      case DataType.DATE:
        return DateFormatUtil.generateSampleValue((properties as DateVariableProperties).format || 'epochMillis');
      case DataType.STRUCTURE:
        const name = (properties as CustomStructureProperties).name;
        const structure: DataStructure | undefined = await this.repository.find(
          query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).where(eq('name', name)).build(),
        );
        if (structure == undefined || structure.properties == undefined) return {};
        return await this.getVariableValueByType(structure.properties);
      default:
        return '';
    }
  };

  private sendEvent = async () => {
    const { selectedTransaction, test, request, key, requestType, id } = this.state;
    try {
      const response = await this.eventService.sendEvent(selectedTransaction, test, request, key, requestType, id);
      if (!response) return;
      this.setState({ response });
      if (!this.isSuccessfulResponse(response)) return;
      const {
        sandbox: { histories },
        setHistories,
      } = this.props;
      if (requestType && request && response.history) {
        const backendHistory = response.history as SandboxHistorySchema;
        const newHistory: SandboxHistorySchema = {
          id: backendHistory.id,
          transactionName: backendHistory.transactionName || selectedTransaction?.name || id || '',
          requestType: backendHistory.requestType || requestType,
          body: backendHistory.body || JSON.parse(request),
          pinned: backendHistory.pinned ?? false,
          createdAt: backendHistory.createdAt || Date.now(),
        };
        const dateKey = moment(newHistory.createdAt).format('DD-MM-YYYY');
        const currentDateHistories = histories?.histories?.[dateKey] || [];
        setHistories({
          pinned: histories?.pinned || [],
          histories: {
            ...histories?.histories,
            [dateKey]: [newHistory, ...currentDateHistories],
          },
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  private isSuccessfulResponse = (response: Record<string, any>): boolean => {
    const status = response?.status;
    if (typeof status === 'number' && (status < 200 || status >= 300)) return false;
    const data = response?.data;
    if (data == null) return true;
    if (data.success === false) return false;
    if (data.error != null) return false;
    return true;
  };

  private onChangeRequest = (request: string | undefined) => {
    this.setState({ request });
  };

  private onChangeKey = (e: ChangeEvent<HTMLInputElement>) => {
    const { request } = this.state;
    if (request != undefined) {
      const req = JSON.parse(request);
      req.key = e.target.value;
      this.setState({ request: JSON.stringify(req, null, 2) });
    }
    this.setState({ key: e.target.value });
  };

  private onChangeId = (e: ChangeEvent<HTMLInputElement>) => {
    const { request } = this.state;
    if (request != undefined && request.trim().length != 0) {
      const req = JSON.parse(request);
      req.id = e.target.value;
      this.setState({ request: JSON.stringify(req, null, 2) });
    }
    this.setState({ id: e.target.value });
  };

  private resetState = () => {
    this.setState({
      id: undefined,
      selectedTransaction: undefined,
      test: false,
      request: undefined,
      response: {},
      key: undefined,
    });
  };
}

interface Props extends FormicaProps, ReduxProps {
  transactions: TransactionJsonModel[];
}

const mapStateToProps = (state: RootState) => ({
  sandbox: state.sandbox,
});
const mapDispatchToProps = { setHistories };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  loading: boolean = true;
  selectedTransaction: TransactionJsonModel | undefined = undefined;
  test: boolean = false;
  request: string | undefined;
  response: Record<string, any> = {};
  requestType: TransactionRequestType | undefined;
  key: string | undefined;
  id: string | undefined;
  codeSnippetVisible: boolean = false;
}

export default withFormica(connector(SandboxApiContainer));
